import { createUseStyles } from 'react-jss';
import { GLOBAL_LETTER_SPACING } from '@/styles/themes/components';
const WHEEL_ITEM_HEIGHT = 30;
const WHEEL_ITEM_HEIGHT_TOUCH = 34;
const WHEEL_ITEM_GAP = 2;
const WHEEL_VISIBLE_ITEMS = 7;
const WHEEL_HEIGHT = WHEEL_ITEM_HEIGHT * WHEEL_VISIBLE_ITEMS +
    WHEEL_ITEM_GAP * (WHEEL_VISIBLE_ITEMS - 1);
const WHEEL_HEADER_HEIGHT = 36;
const WHEEL_BOTTOM_PADDING = WHEEL_HEIGHT - WHEEL_ITEM_HEIGHT;
const WHEEL_BOTTOM_PADDING_TOUCH = WHEEL_HEIGHT - WHEEL_ITEM_HEIGHT_TOUCH;
const WHEEL_TOP_PADDING = 4;
export const useStyles = createUseStyles((theme) => ({
    root: {
        position: 'relative',
        zIndex: 1,
        '&:focus-within': {
            zIndex: theme.zIndex.dropdown,
        },
        '&[data-open="true"]': {
            zIndex: theme.zIndex.max,
        },
    },
    inputWrapper: {
        position: 'relative',
        zIndex: 1,
    },
    field: {
        position: 'relative',
        height: 56,
        minHeight: 56,
        padding: 0,
        border: `1px solid ${theme.colors.textTertiary}`,
        borderRadius: 12,
        backgroundColor: theme.colors.field,
        color: theme.colors.sky,
        cursor: 'text',
        transition: 'none',
        outline: 0,
        '-webkit-tap-highlight-color': 'transparent',
        '&[data-active="true"]': {
            borderColor: theme.colors.brand,
        },
        '&[data-invalid="true"]': {
            borderColor: theme.colors.error,
        },
        [theme.breakpoints.up('sm')]: {
            height: 56,
            minHeight: 56,
        },
        [theme.breakpoints.up('lg')]: {
            height: 48,
            minHeight: 48,
        },
    },
    label: {
        position: 'absolute',
        left: 13,
        top: 12,
        fontSize: '0.8125rem',
        lineHeight: 1,
        color: theme.colors.textPrimary,
        userSelect: 'none',
        pointerEvents: 'none',
        transition: 'none',
        '&[data-active="true"]': {
            color: theme.colors.brand,
        },
        [theme.breakpoints.up('sm')]: {
            left: 14,
        },
        [theme.breakpoints.up('lg')]: {
            top: 8,
            fontSize: '0.75rem',
        },
    },
    timeInput: {
        position: 'absolute',
        left: 13,
        top: 28,
        right: 54,
        height: 22,
        padding: 0,
        border: 0,
        outline: 0,
        appearance: 'none',
        background: 'transparent',
        color: theme.colors.sky,
        fontSize: '0.9375rem',
        lineHeight: 1.37,
        letterSpacing: GLOBAL_LETTER_SPACING,
        fontFamily: 'inherit',
        fontWeight: 400,
        caretColor: theme.colors.sky,
        [theme.breakpoints.up('sm')]: {
            left: 14,
        },
        [theme.breakpoints.up('lg')]: {
            top: 20,
            fontSize: '0.875rem',
        },
        '&::placeholder': {
            color: theme.colors.textSecondary,
            opacity: 1,
        },
        '&::-webkit-input-placeholder': {
            color: theme.colors.textSecondary,
            opacity: 1,
        },
        '&::-moz-placeholder': {
            color: theme.colors.textSecondary,
            opacity: 1,
        },
        '&:-ms-input-placeholder': {
            color: theme.colors.textSecondary,
            opacity: 1,
        },
        '&::selection': {
            background: theme.colors.brandTertiary,
            color: theme.colors.sky,
        },
    },
    postfixWrapper: {
        position: 'absolute',
        top: 0,
        right: 14,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    timeButton: {
        width: 32,
        height: 32,
        marginRight: -5,
        padding: 0,
        border: 0,
        outline: 0,
        borderRadius: 999,
        appearance: 'none',
        background: 'transparent',
        color: theme.colors.sky,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        touchAction: 'manipulation',
        userSelect: 'none',
        transition: theme.transition.global,
        '-webkit-tap-highlight-color': 'transparent',
        '-webkit-touch-callout': 'none',
        '&[data-open="true"]': {
            color: theme.colors.white,
        },
        '&:hover:not([data-open="true"])': {
            backgroundColor: theme.mode === 'dark'
                ? theme.colors.textBackground
                : theme.colors.brandTertiary,
        },
        '&:focus, &:focus-visible, &:active': {
            outline: 0,
            boxShadow: 'none',
        },
        '& *': {
            '-webkit-tap-highlight-color': 'transparent',
        },
    },
    postfixIcon: {
        position: 'absolute',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexShrink: 0,
        transform: 'translateX(3px)',
        '& svg': {
            width: 22,
            height: 22,
        },
        '& svg > path': {
            fill: theme.colors.brand,
        },
    },
    postfixConfirm: {
        position: 'absolute',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexShrink: 0,
        transform: 'translateX(3px)',
        width: 32,
        height: 32,
        backgroundColor: theme.colors.brand,
        transition: 'background-color .2s ease',
        borderRadius: 999,
        '& svg': {
            width: 24,
            height: 24,
        },
        '& svg > path': {
            fill: theme.colors.white,
        },
        '$timeButton:hover &': {
            backgroundColor: theme.colors.brand,
        },
    },
    popover: {
        position: 'absolute',
        top: 'calc(100% + 6px)',
        left: 'calc(50% - 130px)',
        width: 260,
        maxWidth: 'calc(100vw - 32px)',
        height: WHEEL_HEIGHT + WHEEL_HEADER_HEIGHT + 24,
        padding: '12px',
        borderRadius: 12,
        background: theme.colors.cloud,
        boxShadow: theme.elevations.dropdown,
        userSelect: 'none',
        overflow: 'hidden',
        zIndex: theme.zIndex.max,
        [theme.breakpoints.down('lg')]: {
            left: 0,
            width: '100%',
            minWidth: 220,
        },
    },
    wheels: {
        position: 'relative',
        height: WHEEL_HEIGHT,
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        columnGap: 12,
        zIndex: 1,
    },
    wheelHeaders: {
        position: 'relative',
        height: WHEEL_HEADER_HEIGHT,
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        alignItems: 'center',
        columnGap: 12,
        margin: '-12px -12px 0',
        padding: '0 20px',
        background: theme.colors.textBackground,
        color: theme.colors.textPrimary,
        fontSize: 13,
        letterSpacing: GLOBAL_LETTER_SPACING,
        zIndex: 3,
        '& span:first-child': {
            gridColumn: 1,
            textAlign: 'center',
        },
        '& span:last-child': {
            gridColumn: 2,
            textAlign: 'center',
        },
    },
    wheelColumn: {
        position: 'relative',
        height: WHEEL_HEIGHT,
    },
    wheel: {
        height: WHEEL_HEIGHT,
        paddingTop: WHEEL_TOP_PADDING,
        paddingBottom: WHEEL_BOTTOM_PADDING,
        overflowY: 'auto',
        boxSizing: 'border-box',
        scrollbarWidth: 'thin',
        scrollbarColor: `${theme.colors.textTertiary} transparent`,
        scrollBehavior: 'smooth',
        overscrollBehavior: 'contain',
        '-webkit-overflow-scrolling': 'touch',
        '&::-webkit-scrollbar': {
            width: 6,
        },
        '&::-webkit-scrollbar-thumb': {
            borderRadius: 6,
            background: theme.colors.textTertiary,
        },
        '&::-webkit-scrollbar-track': {
            background: 'transparent',
        },
        [theme.breakpoints.down('md')]: {
            paddingBottom: WHEEL_BOTTOM_PADDING_TOUCH,
        },
    },
    wheelItem: {
        width: 'calc(100% - 8px)',
        height: WHEEL_ITEM_HEIGHT,
        margin: `0 4px ${WHEEL_ITEM_GAP}px`,
        padding: 0,
        border: '1px solid transparent',
        borderRadius: 8,
        background: 'transparent',
        color: theme.colors.textPrimary,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 14,
        fontWeight: 500,
        letterSpacing: GLOBAL_LETTER_SPACING,
        opacity: 1,
        transition: 'none',
        '-webkit-tap-highlight-color': 'transparent',
        '&[data-selected="true"]': {
            color: theme.colors.brand,
            borderColor: theme.colors.brand,
            background: 'transparent',
        },
        [theme.breakpoints.down('md')]: {
            height: WHEEL_ITEM_HEIGHT_TOUCH,
        },
    },
}), { name: 'flight-time-picker-input' });
